import { authentication } from '../models/Authentication';
import { userProfile } from '../models/UserProfile';
import { clientCredentialsToken } from '../models/ClientCredentialsToken';
import { generateBody, jsonOrThrow, jsonOrThrowWithMessage } from '../utils/Helpers';
import { BASIC_TOKEN } from '../constants/APIKeys-obfuscated';
import { DOMAIN } from '../constants/URLs'; // import DOMAIN instead on server-change
import { GET_VERSION_ERROR } from '../constants/ErrorMessages';
import moment from 'moment';
import { projectConfig } from '../project-info/projectConfig';
import { CHALLENGE_TYPES } from '../constants/ChallengeTypes';
import { tenantProjectInfo } from '../models/TenantProjectInfo';

/* DB APIs */

// Cache for analytics data
const analyticsCache = {
  data: null,
  timestamp: null,
  TTL: 3600000 * 24, // 24 hours in milliseconds (3600 seconds)
};

export class rwbApi {
  // Authentication

  static fetchAuthToken(username, password) {
    const url = `${DOMAIN}api/token`;
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: username, password: password }),
    })
      .then(async (response) => {
        if (!response.ok) {
          let msg = `Response returned ${response.status}`;
          try {
            const t = await response.text();
            if (t) msg += `: ${t.substring(0,120)}`;
          } catch (_) {}
          throw new Error(msg);
        }
        return response.json();
      })
      .then((oauthResponse) => {
        return authentication.setAuthentication(oauthResponse).then((success) => {
          if (success) {
            return oauthResponse;
          } else throw new Error(`Couldn't save credentials.`);
        });
      });
  }


  static getAuthentikRedirect(email, redirect_for) {
    return fetch(`${DOMAIN}api/authentik/redirect?email=${encodeURIComponent(email)}&redirect_for=${encodeURIComponent(redirect_for)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(jsonOrThrowWithMessage);
  }

  static getAuthentikToken(token, state) {
    const q = `token=${encodeURIComponent(token)}&state=${encodeURIComponent(state)}`;
    return fetch(`${DOMAIN}api/authentik/token?${q}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(jsonOrThrowWithMessage).then((oauthResponse) => {
        return authentication
          .setAuthentication(oauthResponse)
          .then((success) => {
            if (success) {
              return oauthResponse;
            } else throw new Error(`Couldn't save credentials.`);
          })
          .then((_response) => {
            return rwbApi.getAuthenticatedUser();
          });
      });
  }

  static refreshAuth(refreshToken) {
    return fetch(`${DOMAIN}api/token/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    }).then(jsonOrThrow);
  }

  static getAuthenticatedUser() {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(`${DOMAIN}api/user/me`, {
          method: 'GET',
          headers: {
            Accepts: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then(jsonOrThrow)
      .then((json) => {
        if (json.tenant) tenantProjectInfo.saveToTenantProjectInfo(json.tenant);
        return userProfile.saveToUserProfile(json);
      })
      .then(rwbApi.getUser);
  }

  static getClientCredentialsToken() {
    return fetch(`${DOMAIN}?oauth=token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Basic ${BASIC_TOKEN}`,
      },
      body: generateBody({ grant_type: 'client_credentials' }),
    })
      .then(jsonOrThrow)
      .then((json) => {
        clientCredentialsToken.setAccessToken(json.access_token);
        return json;
      });
  }

  // User Creation

  static loginUser(email, password, options = { skipFetchMe: false }) {
    return rwbApi
      .fetchAuthToken(email, password)
      .then((response) => {
        if (options && options.skipFetchMe) {
          return true;
        }
        return rwbApi.getAuthenticatedUser();
      })
      .catch((error) => {
        throw error;
      });
  }

  // Need fine usage of error codes in createUser and createNewUser
  // Do not use jsonOrThrow on these calls.

  static createUser(body) {
    return fetch(`${DOMAIN}${projectConfig.apiVersion}user`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });
  }

  static createNewUser(body) {
    return rwbApi.createUser(body);
  }

  // Returns both an access token and a new user ID
  static createUserFromOneAll(user_token, identity_token) {
    const body = {
      user_token,
      identity_token,
    };
    return fetch(`${DOMAIN}${projectConfig.apiVersion}user/oneall_token`, {
      method: 'POST',
      headers: {
        'Content-Type': `application/json`,
        'Content-Length': JSON.stringify(body).length,
      },
      body: JSON.stringify(body),
    });
  }

  static createNewUserFromOneAll(user_token, identity_token) {
    return rwbApi.createUserFromOneAll(user_token, identity_token);
  }

  static verifyEmail(code) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(`${DOMAIN}${projectConfig.apiVersion}user/verify`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ verification_code: code }),
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error('Unable to verify email.');
        }
      });
    });
  }

  static verifyWorkspaceAccessCode(code) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(`${DOMAIN}${projectConfig.apiVersion}user/access_code`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({access_code: code}),
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error('Unable to verify code.');
        }
      });
    });
  }
  
  // General Events Information

  static loadNextURL(url) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.ok) return response.json();
        else throw Error('Load more error');
      });
  }

  static getMobileEvents(urlParams) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}event${urlParams.length ? `?${urlParams}` : ``
          }`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              // specific cache issue when looking at "My Events"
              'Cache-Control': urlParams.includes('host_id')
                ? 'no-cache'
                : null,
            },
          },
        );
      })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        // trying to load a page past what exists will result in a 400
        else if (response.status === 400) {
          return [];
        } else throw Error('Server Error');
      });
  }

  static getMobileEvent(id, forceRefresh = false) {
    const url = `${DOMAIN}${projectConfig.apiVersion}event/${id}`;
    return authentication.getAccessToken().then((accessToken) => {
      let headers = {
        Authorization: `Bearer ${accessToken}`,
      };
      if (forceRefresh) headers['Cache-Control'] = 'no-cache';
      return fetch(url, {
        method: 'GET',
        headers,
      }).then(jsonOrThrow);
    });
  }

  static removeEvent(event_id) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(`${DOMAIN}${projectConfig.apiVersion}event/${event_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error removing event');
      });
    });
  }

  // User Information

  static getUser() {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static getUserByID(id) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(`${DOMAIN}${projectConfig.apiVersion}user/${id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static putUser(data) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Content-Length': data.length,
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        },
      )
        .then(jsonOrThrow)
        .then((json) => {
          return userProfile.saveToUserProfile(json).then(() => {
            return json;
          });
        });
    });
  }

  static putUserPhotoWeb(data) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        const options = {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        };
        delete options.headers['Content-Type'];
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
          }/photo`,
          options,
        );
      })
      .then(jsonOrThrow);
  }

  static putUserPhoto(data) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
          }/photo`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${accessToken}`,
            },
            body: data,
          },
        );
      })
      .then(jsonOrThrow);
  }

  static getAppVersion() {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/app_version`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((result) => {
        if (result.ok) {
          return result.json();
        } else {
          return `${GET_VERSION_ERROR}`;
        }
      });
    });
  }

  static updateAppVersion(version, platform) {
    const data =
      platform === 'web'
        ? {
          web_version: version,
        }
        : {
          mobile_version: version,
        };
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/app_version`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Content-Length': data.length,
          },
          body: JSON.stringify(data),
        },
      ).then((result) => {
        if (result.ok) {
          return result.json();
        } else {
          return `${GET_VERSION_ERROR}`;
        }
      });
    });
  }

  static getMinimumRequiredVersion() {
    return fetch(`${DOMAIN}${projectConfig.apiVersion}minimum_version`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
      },
    }).then((result) => {
      if (result.ok) {
        return result.json();
      } else {
        throw new Error('Error retrieving minimum required version.');
      }
    });
  }

  // User-Specific Event Endpoints

  // DEPRECATED: THIS ONLY CONNECTS TO THE OLD EVENTS TABLE
  // Can be called from a notification
  // Must wait for userProfile to fetch data from async storage before calling
  static putAttendanceStatus(eventid, status) {
    console.warn(
      'Deprecated call. Find the appropriate mobile endpoint if working with new events database.',
    );
    return userProfile.init().then((userData) => {
      if (userData.id === null) {
        throw new Error(`NO USER LOGGED IN`);
      }
      const data = JSON.stringify({
        status: status,
      });
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/event/${eventid}`;
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(url, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Content-Length': data.length,
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        });
      });
    });
  }

  /* MOBILE ENDPOINTS */

  // seems unused as of 08/29/2024
  static getMobileAttendees(event_id, status, page) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        const url = `${DOMAIN}${projectConfig.apiVersion}event/${event_id}/attendee?status=${status}&page=${page}`;
        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return Error(`Error retrieving Attendees with ${status} status`);
        }
      });
  }

  static getAllMobileAttendees(event_id) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        const url = `${DOMAIN}${projectConfig.apiVersion}event/${event_id}/attendee/all`;
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else return Error('Unable to retrieve mobile attendees');
      });
  }

  static getAllMobileAttendeesCsv(event_id) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        const url = `${DOMAIN}${projectConfig.apiVersion}event/${event_id}/attendee/csv`;
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else return Error('Unable to retrieve mobile attendee CSV');
      });
  }


  static getAllFollowedAttendees(event_id) {
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
      }/followed_attendees?event_ids=${event_id}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieved followed attendees');
        }
      });
  }

  static getMobileAttendanceStatus(eventid) {
    const url = `${DOMAIN}${projectConfig.apiVersion
      }event/${eventid}/attendee/${userProfile.getUserProfile().id}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.json().then((result) => {
            return result;
          });
        } else if (response.status === 404) return '';
        // no `status` was found
        else return Error('Unable to retrieve mobile attendance status');
      });
  }

  // Can be called from a notification
  // Must wait for userProfile to fetch data from async storage before calling
  static updateMobileAttendanceStatus(eventid, status) {
    return userProfile.init().then((userData) => {
      if (userData.id === null) {
        throw new Error(`NO USER LOGGED IN`);
      }
      const data = JSON.stringify({
        status: status,
      });
      const url = `${DOMAIN}${projectConfig.apiVersion}event/${eventid}/attendee/${userData.id}`;
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(url, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Content-Length': data.length,
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        });
      });
    });
  }

  static setInitialMobileAttendanceStatus(eventid, status) {
    const data = JSON.stringify({
      status: status,
    });
    const url = `${DOMAIN}${projectConfig.apiVersion
      }event/${eventid}/attendee/${userProfile.getUserProfile().id}`;
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Content-Length': data.length,
          Authorization: `Bearer ${accessToken}`,
        },
        body: data,
      });
    });
  }

  // Push Token

  static putPushToken(token, device_id) {
    return authentication.getAccessToken().then((accessToken) => {
      const body = JSON.stringify({ token, device_id });
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/push_token`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Content-Length': body.length,
        },
        body,
      });
    });
  }

  static putFCMToken(fcm_token, device_id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/fcm_token`;
      const body = JSON.stringify({ fcm_token, device_id });
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });
    });
  }

  // Chapter manipulation

  static getChapters() {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        const url = `${DOMAIN}${projectConfig.apiVersion}chapter`;
        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then(jsonOrThrow);
  }

  static putChapters(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url =
        DOMAIN +
        `${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/chapter-settings`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      })
        .then(jsonOrThrow)
        .then((json) => {
          return userProfile.saveToUserProfile(json).then(() => {
            return json;
          });
        });
    });
  }

  static postEvent(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}event`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      })
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static putEvent(id, data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}event/${id}`;
      return fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      })
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static patchEvent(id, data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}event/${id}`;
      return fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      })
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static putFeed(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url =
        DOMAIN +
        `${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/feed`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Unable to create post');
        }
      });
    });
  }

  static deleteUserPost(streamID) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/feed/${streamID}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then((response) => {
          if (!response.ok) return Error('Error deleting post');
        })
        .catch((err) => {
          console.warn(err);
        });
    });
  }

  static deleteEventPost(eventID, streamID) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}event/${eventID}/feed/${streamID}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then((result) => {
          if (result.ok) return;
          else throw new Error('Error deleting event post');
        })
        .catch((err) => {
          // Android's OkHttp throws "Network request failed" for 204 No Content responses.
          // The delete succeeded on the server; swallow this false positive.
          if (err.message !== 'Network request failed') throw err;
        });
    });
  }

  // only activities performed by the target user
  static getUserFeed(id, offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}user/${id}/feed`;
      if (offset) url += `?offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Event Feed');
        }
      });
    });
  }

  // only activites performed by the target user's following
  // this will always be the logged in user
  static getTimelineFeed(offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url =
        DOMAIN +
        `${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/news?kind=aggregated`;
      if (offset) url += `&offset=${offset}`;
      url += `&limit=10`; // these limits aren't in RWB, and aren't in getUserFeed. Are they needed?
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Feed');
        }
      });
    });
  }
  // get all activities from all users
  static getEverythingFeed(offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = DOMAIN + `${projectConfig.apiVersion}timeline/everything`;
      url += `?limit=10`;
      if (offset) url += `&offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Feed');
        }
      });
    });
  }

  static getSpecificPost = (userID, streamID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userID}/feed/${streamID}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.ok) return response.json();
        else throw new Error('Error retrieving specific event');
      });
    });
  };

  static getPinnedPost() {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/feed/pinned_post`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((response) => {
        if (response.ok) return response.json();
        else return [];
      });
    });
  }

  static createEventPost(data, id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}event/${id}/feed`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then(jsonOrThrow);
    });
  }

  static getEventFeed(id, offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}event/${id}/feed`;
      if (offset) url += `?offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Event Feed');
        }
      });
    });
  }

  // general user search
  static searchUser(input) {
    return authentication.getAccessToken().then((accessToken) => {
      const chapterID = userProfile.getUserProfile().preferred_chapter.id;
      let url = `${DOMAIN}${projectConfig.apiVersion}user/search?q=${input}`;
      // if chapterID is null, do not send that to avoid an error
      if (chapterID) url += `&chapter_id=${chapterID}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json().then((result) => {
            return result.data;
          });
        } else {
          return [];
        }
      });
    });
  }

  // used to search members in a group
  static searchGroupMembers(input, groupID) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}group/${groupID}/user/search?q=${input}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json().then((result) => {
            return result.data;
          });
        } else {
          return Error('Unable to search group members');
        }
      });
    });
  }

  static getFollows = (id, relation, offset) => {
    if (!id) return Error('User ID is required');
    const url =
      DOMAIN +
      `${projectConfig.apiVersion}user/${id}/follow?relation=${relation}&offset=${offset}`;
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      })
        .then((response) => {
          if (response.ok) {
            return response
              .json()
              .then((result) => {
                return result.data;
              })
              .catch((error) => {
                console.warn(error);
              });
          } else {
            console.warn(response);
          }
        })
        .catch((error) => {
          console.warn(error);
        });
    });
  };

  static getFollowSummary = (id) => {
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${id}/follow/summary`;
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      })
        .then((response) => {
          if (response.ok) {
            return response
              .json()
              .then((result) => {
                return result;
              })
              .catch((error) => {
                console.error(error);
              });
          } else {
            console.error(response);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    });
  };

  static getFollowingRelationships = (source, target) => {
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${source}/follow/${target}`;
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then((response) => {
          if (response.ok) {
            return response
              .json()
              .then((result) => {
                return result;
              })
              .catch((error) => {
                console.error(error);
              });
          } else {
            console.error(response);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    });
  };

  static followUser(target_id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url =
        DOMAIN +
        `${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/follow/${target_id}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then(jsonOrThrow)
        .then((json) => {
          return json;
        });
    });
  }

  static unfollowUser(target_id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url =
        DOMAIN +
        `${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/follow/${target_id}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((json) => {
        return json;
      });
    });
  }

  static getMediaUploadURL(data) {
    let updatedData = JSON.parse(data);
    // add the date to images to prevent overwritten of images with the same name
    updatedData.media_filename = `${new Date().toISOString()}-${updatedData.media_filename
      }`;
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}media`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      }).then((result) => {
        if (result.status === 200) {
          return result.json();
        } else {
          throw new Error('Unable to get image URL');
        }
      });
    });
  }

  // Notifications
  static getNotifications = (offset) => {
    let url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
      }/notifications?items=25`;
    if (offset) url += `&offset=${offset}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.ok) return response.json();
        else throw new Error('Error retrieving Notifications');
      });
  };

  static getUnseenNotifications = () => {
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
      }/notifications/check`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.ok) return response.json();
        else {
          console.warn(response);
          throw new Error('Error retrieving unseen Notifications');
        }
      });
  };

  // Reactions
  // This is used on a single post. Client side it is used to retrieve updated reactions on pull refresh
  static getReactions = (creatorID, streamID, offset, kind) => {
    let url = `${DOMAIN}${projectConfig.apiVersion}user/${creatorID}/reactions/${streamID}`;
    if (offset) url += `?offset=${offset}`;
    if (kind) url += `${offset ? '&' : '?'}kind=${kind}`; // if both optional params are passed, should be &
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache', //not sure if needed, but not sure if side effects with this get
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.ok) return response.json();
        else {
          console.warn(response);
          throw new Error('Error retrieving Reactions');
        }
      });
  };

  static postReaction = (creatorID, streamID, data) => {
    if (!JSON.parse(data).kind) throw new Error('Reaction Kind is required');
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${creatorID}/reactions/${streamID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: data,
        });
      })
      .then((response) => {
        return response.json().then((result) => {
          if (response.ok) return result;
          else throw result.error.message;
        });
      });
  };

  // will be used to "unlike" a post or delete a comment
  static deleteReaction = (creatorID, streamID, data, commentID) => {
    if (!JSON.parse(data).kind) throw new Error('Reaction Kind is required');
    let url = `${DOMAIN}${projectConfig.apiVersion}user/${creatorID}/reactions/${streamID}`;
    if (commentID) url += `/${commentID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: data,
        });
      })
      .then((response) => {
        if (!response.ok) return Error('Error deleting reaction');
      })
      .catch((err) => {
        console.warn(err);
      });
  };

  static updateReaction = (creatorID, streamID, data, commentID) => {
    if (JSON.parse(data).kind != 'comment')
      throw new Error('Comment Kind is required');
    let url = `${DOMAIN}${projectConfig.apiVersion}user/${creatorID}/reactions/${streamID}/${commentID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: data,
        });
      })
      .then((response) => {
        return response.json().then((result) => {
          if (response.ok) return result.data;
          else throw result.error.message;
        });
      });
  };

  // Event Reactions
  static getEventReactions = (eventID, streamID) => {
    const url = `${DOMAIN}${projectConfig.apiVersion}event/${eventID}/reactions/${streamID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache', //not sure if needed, but not sure if side effects with this get
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.ok) return response.json();
        else {
          console.warn(response);
          throw new Error('Error retrieving Reactions');
        }
      });
  };

  static postEventReaction = (eventID, streamID, data) => {
    if (!JSON.parse(data).kind) throw new Error('Reaction Kind is required');
    const url = `${DOMAIN}${projectConfig.apiVersion}event/${eventID}/reactions/${streamID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: data,
        });
      })
      .then((response) => {
        if (response.ok) return;
        else {
          return response.json().then((result) => {
            throw result.error.message;
          });
        }
      });
  };

  // will be used to "unlike" a post
  static deleteEventReaction = (eventID, streamID, data) => {
    if (!JSON.parse(data).kind) throw new Error('Reaction Kind is required');
    const url = `${DOMAIN}${projectConfig.apiVersion}event/${eventID}/reactions/${streamID}`;
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(url, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: data,
        });
      })
      .then((response) => {
        if (response.ok) return;
        else {
          return response.json().then((result) => {
            throw result.error.message;
          });
        }
      });
  };

  static updatePost = (data, id) => {
    const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
      }/feed/${id}`;
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.status === 200) return response.json();
        else return Error('Unable to update post.');
      });
    });
  };

  static reportUser(reportedID, reporterID) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(`${DOMAIN}${projectConfig.apiVersion}report/user/${reportedID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterID,
      })
        .then((response) => {
          if (response.ok) return response.json();
          else throw new Error('Error reporting user');
        })
        .catch((err) => {
          console.warn(err);
          throw new Error('Error reporting user');
        });
    });
  }

  static reportUserPost = (reportedID, reportedStreamID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}report/user/${reportedID}/post/${reportedStreamID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting user post');
      });
    });
  };

  static reportEvent = (eventID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}report/event/${eventID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting the event');
      });
    });
  };

  static reportEventPost = (eventID, streamID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}report/event/${eventID}/post/${streamID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting the event post');
      });
    });
  };

  static reportUserComment = (reportedID, reactionID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}report/user/${reportedID}/reaction/${reactionID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting the comment');
      });
    });
  };

  // currently unused
  static reportGroup = (groupID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting the group');
      });
    });
  };

  static reportGroupPost = (groupID, streamID, reporterUserID) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupID}/post/${streamID}`;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: reporterUserID,
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Error reporting the group');
      });
    });
  };

  // groups
  static joinGroup = (groupId) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupId}/user/${userProfile.getUserProfile().id
        }`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Unable to join the group');
      });
    });
  };

  static leaveGroup = (groupId) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupId}/user/${userProfile.getUserProfile().id
        }`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return;
        else throw new Error('Unable to leave the group');
      });
    });
  };

  // more to do
  // type can be "nearby", "my", "favorites"
  static retrieveGroups = (type, nearbyData) => {
    if (!type) throw new Error('Type is required to retrieve groups');
    let url = `${DOMAIN}${projectConfig.apiVersion}group?type=${type}`;
    if (type === 'nearby' && nearbyData) {
      // nearby data is either a lat/long object, or chapter id
      if (nearbyData.chapter_id) url += `&chapter_id=${nearbyData.chapter_id}`;
      else url += `&lat=${nearbyData.lat}&long=${nearbyData.long}`;
    }
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Unable to retrieve groups');
      });
    });
  };

  static getGroup = (groupId) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupId}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Unable to load the group');
      });
    });
  };

  // check if the user has joined and favorited this group
  static getGroupRelation = (userId, groupId) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userId}/group/${groupId}/relation`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Unable to load the group relationship');
      });
    });
  };

  /**
   * Lookup groups
   * @param {string} input - user input, expecting a group name
   * @param {object} locationObj - an object containing either the lat and long, or the chapter_id associated with the user when lat/long cannot be accessed
   * @param {int} offset - which iteration of results should be retrieved, starting at 0
   */

  static searchGroups = (input, locationObj, offset) => {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}group/search`;
      if (input) url += `?${input}`;
      // if (locationObj.chapter_id) url += `&chapter_id=${locationObj.chapter_id}`;
      // else url += `&lat=${locationObj.lat}&long=${locationObj.long}`;
      // if (offset) url += `&offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache', // cache disabled to ensure list shows updated groups joined/left
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Unable to find groups');
      });
    });
  };

  static retrieveGroupMembers = (groupId, role, offset) => {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupId}/user/search?role=${role}&offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Unable to retrieve group member list');
      });
    });
  };

  static getGroupFeed(id, offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}group/${id}/feed`;
      if (offset) url += `?offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Group Feed');
        }
      });
    });
  }

  static createGroupPost(data, id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${id}/feed`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.status === 200) return response.json();
        else return Error('Unable to post to group.');
      });
    });
  }

  static isGroupAdmin(groupId) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupId}/user/${userProfile.getUserProfile().id}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache', // cache disabled to ensure it shows updated value
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json().then((result) => {
            return result.role === 'Admin';
          });
        }
        else
          throw Error('Unable to retrieve if user is an admin of the group.');
      });
    });
  }

  static deleteGroupPost(groupID, streamID) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}group/${groupID}/feed/${streamID}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then((result) => {
          if (result.ok) return;
          else throw new Error('Error deleting group post');
        })
        .catch((err) => {
          if (err.message !== 'Network request failed') throw err;
        });
    });
  }

  // stripe

  // send data to backend to have it create the payment intent and provide the client secret
  static createPaymentIntent(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}stripe`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.ok) return response.json();
        else {
          return response.json().then((error) => {
            throw error;
          });
        }
      });
    });
  }

  static createPayment(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/purchase`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.ok) {
          return response.json();
        }
        else {
          return response.json().then((error) => {
            throw error;
          });
        }
      });
    });
  }

  static getNotificationSettings() {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/push_settings`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((result) => {
        if (result.ok) {
          return result.json();
        } else throw new Error('Error retrieving notification settings');
      });
    });
  }

  static updateNotificationSettings(data) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/push_settings`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Content-Length': data.length,
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        },
      ).then((result) => {
        if (result.ok) {
          return result.json();
        } else throw new Error('Error updating notification settings');
      });
    });
  }

  static getChallenges(urlParams) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/challenge`;
      if (urlParams) url += urlParams;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'cache-control': 'no-cache', // ensure the user sees their newly joined challenge
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving challenges');
      });
    });
  }

  static getChallenge(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion
        }challenge/${challengeId}?after=${moment().toISOString()}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving challenge');
      });
    });
  }

  static getChallengeParticipants(challengeId, page = 1) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}challenge/${challengeId}/participants?page=${page}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else if (result.status === 404) return { participants: [] };
        else throw new Error('Error retrieving challenge participants');
      });
    });
  }

  static getChallengeFollowingParticipants(challengeIds) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id
        }/followed_participants?challenge_ids=${challengeIds}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else if (result.status === 404) return [{ attendees: [] }];
        else throw new Error('Error retrieving followers for challenges');
      });
    });
  }

  static joinChallenge(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion
        }challenge/${challengeId}/participants/${userProfile.getUserProfile().id
        }`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((result) => {
        if (!result.ok) throw new Error('Error joining challenge');
      });
    });
  }

  static leaveChallenge(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion
        }challenge/${challengeId}/participants/${userProfile.getUserProfile().id
        }`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((result) => {
        if (!result.ok) throw new Error('Error joining challenge');
      });
    });
  }

  static hasJoinedChallenge(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion
        }challenge/${challengeId}/participants/${userProfile.getUserProfile().id
        }`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.status === 404) return false;
        else if (result.status === 200) return true;
        else throw new Error('Error getting if user has joined the challenge');
      });
    });
  }

  // checking if the current has earned the badge for a specific challenge. Returned badge info is not needed.
  static getBadgeStatus(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}api/v1/user/${userProfile.getUserProfile().id
        }/challenge/${challengeId}/badges`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.status === 404) return false;
        else if (result.status === 200) return true;
        else
          throw new Error(
            'Unable to determine if the user has acquired the badge for the associated challenge',
          );
      });
    });
  }

  static putWorkout(data) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id}/workout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: data,
        },
      ).then((response) => {
        if (response.ok) return;
        return response.json().then(result => {
          // potentially could check for other error types and return first element in error array, currently unsure what other errors look like
          if (result?.ounces_of_water?.length === 1) {
            throw new Error(result?.ounces_of_water[0]);
          } else {
            throw new Error('Creating workout');
          }
        });
      });
    });
  }


  /* send a list of workouts. one per day. This will update days that you resend */
  static updateDailyWorkout(workoutDataList) {
    const profileId = userProfile.getUserProfile().id;

    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${profileId}/workouts/daily`,
        {
          method: 'PUT', // Using PUT for update
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(workoutDataList),
        },
      ).then((response) => {
        if (response.ok) return response.json();
        else throw new Error('Error updating daily workouts');
      });
    });
  }

  static deleteWorkout(workoutId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id}/workout/${workoutId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((response) => {
        if (response.status === 204) {
          return;
        } else {
          throw new Error('Error deleting workout');
        }
      }).catch((err) => {
        console.error('Error caught in fetch operation: ', err);
      });
    });
  }

  static getWorkout(workoutId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}workout/${workoutId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving workout');
      });
    });
  }

  static getUserWorkouts(challengeId = null, challengeType = null) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}user/${userProfile.getUserProfile().id}/workouts`;
      if (challengeId) {
        url = `${url}?challenge_id=${challengeId}`;
        if (challengeType === CHALLENGE_TYPES.hydration) {
          url = `${url}&ounces_of_water__gt=0`;
        } else {
          url = `${url}&ounces_of_water=0`;
        }
      }
      return fetch(
        url,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving user workouts');
      });
    });
  }

  static getLeaderboardRank(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${
          projectConfig.apiVersion
        }leaderboard/challenge/${challengeId}/user/${
          userProfile.getUserProfile().id
        }/rank`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving user ranking');
      });
    });
  }

  static getLeaderboardTop(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${projectConfig.apiVersion}leaderboard/challenge/${challengeId}/top25`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving leaderboard top 25');
      });
    });
  }

  static getLeaderboardCentered(challengeId) {
    return authentication.getAccessToken().then((accessToken) => {
      return fetch(
        `${DOMAIN}${
          projectConfig.apiVersion
        }leaderboard/challenge/${challengeId}/user/${
          userProfile.getUserProfile().id
        }/centered`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        },
      ).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving user centered 25');
      });
    });
  }

  static getChallengeFeed(id, offset) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}challenge/${id}/feed`;
      if (offset) url += `?offset=${offset}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 404) {
          return [];
        } else {
          return Error('Unable to retrieve Challenge Feed');
        }
      });
    });
  }

  static createChallengePost(data, id) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}challenge/${id}/feed`;
      return fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.status === 200) return response.json();
        else return Error('Unable to post to challenge.');
      });
    });
  }

  static deleteChallengePost(id, streamId) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}${projectConfig.apiVersion}challenge/${id}/feed/${streamId}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        if (!response.ok) return Error('Error deleting post');
      })
      .catch((err) => {
        console.warn(err);
      });
    });
  }

  static getUserBadges(userId) {
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}user/${userId}/badges`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving user badges');
      });
    });
  }

  static getUserMetrics() {
    return authentication.getAccessToken().then((accessToken) => {
      const d = new Date();
      let url = `${DOMAIN}${projectConfig.apiVersion}user/${
        userProfile.getUserProfile().id
      }/workout/metrics?month=${d.getFullYear()}-${d.getMonth() + 1}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
        },
      }).then((result) => {
        if (result.ok) return result.json();
        else throw new Error('Error retrieving user year to date metrics');
      });
    });
  }

  static deleteAccount(data) {
    return authentication.getAccessToken().then((accessToken) => {
      const url = `${DOMAIN}api/user/${userProfile.getUserProfile().id}`;
      return fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: data,
      }).then((response) => {
        if (response.ok) {
          return true;
        } else if (response.status === 400) {
          return false;
        } else {
          throw new Error('Unable to delete account.');
        }
      });
    });
  }

    // Block and Unblock User Endpoints

    static getListOfBlockedUsers() {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(
          `${DOMAIN}api/user/${userProfile.getUserProfile().id}/block`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        ).then((response) => {
          if (response.status === 200) {
            return response.json();
          } else {
            throw new Error('Error retrieving blocked users.');
          }
        });
      });
    }

    static unblockUser(target_id) {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(
          `${DOMAIN}api/user/${
            userProfile.getUserProfile().id
          }/block/${target_id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: target_id,
          },
        ).then((response) => {
          if (response.status === 204) {
            return;
          } else {
            throw new Error('Error unblocking user.');
          }
        });
      });
    }

    static blockUser(target_id) {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(
          `${DOMAIN}api/user/${
            userProfile.getUserProfile().id
          }/block/${target_id}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: target_id,
          },
        ).then((response) => {
          if (response.status === 201) {
            return response.json();
          } else {
            throw new Error('Error blocking user.');
          }
        });
      });
    }

    static blockPost(activityID) {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(
          `${DOMAIN}api/user/${
            userProfile.getUserProfile().id
          }/block_post/${activityID}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        ).then((response) => {
          if (response.status === 201) {
            return response.json();
          } else {
            throw new Error('Error blocking post.');
          }
        });
      });
    }

    // get post like user likes list
    static getLikeUserList(userId, postId, payload = {}, next) {
      return authentication.getAccessToken().then((accessToken) => {
        let url = `${DOMAIN}api/user/${userId}/reactions/${postId}/list?${new URLSearchParams(
          payload,
        ).toString()}`;
        if (next) url += `&offset=${next}`;
        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        }).then((result) => {
          if (result.ok) {
            return result.json();
          } else {
            throw new Error('Error retrieving user likes list');
          }
        });
      });
    }

    static getSubscriptionOptions() {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(`${DOMAIN}api/purchase_options`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }).then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            return Error('Error retrieving subscription options.');
          }
        });
      });
    }

    static getDiscountRate() {
      return authentication.getAccessToken().then((accessToken) => {
        return fetch(`${DOMAIN}api/discount`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }).then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            return Error('Error retrieving subscription options.');
          }
        });
      });
    }

  static getUsageAnalytics({startDate, endDate, forceRefresh = false} = {startDate: null, endDate: null, forceRefresh: false}) {
    const now = Date.now();
    
    // Check if we have valid cached data (unless force refresh is requested)
    if (
      !forceRefresh &&
      analyticsCache.data &&
      analyticsCache.timestamp &&
      (now - analyticsCache.timestamp) < analyticsCache.TTL
    ) {
      return Promise.resolve(analyticsCache.data);
    }
    
    return authentication.getAccessToken().then((accessToken) => {
      let url = `${DOMAIN}${projectConfig.apiVersion}analytics`;
      if (startDate) url += `?date_start=${startDate}`;
      if (endDate) url += `${startDate ? '&' : '?'}date_end=${endDate}`;
      return fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => {
        if (result.ok) {
          return result.json().then((data) => {
            // Cache the successful response
            if (!forceRefresh) {
              analyticsCache.data = data;
              analyticsCache.timestamp = Date.now();
            }
            return data;
          });
        } else {
          throw new Error('Error retrieving usage analytics.');
        }
      });
    });
  }
  
  static getChatLogs(identifier) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}get_chat_logs/${identifier}`,
          {
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache',
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
      })
      .then(jsonOrThrow);
  }
  static getChatToken(identifier) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}get_chat_token`,
          {
            method: 'POST',
            headers: {
              'Cache-Control': 'no-cache',
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ identifier: identifier }),
          },
        );
      }).then((result) => {
        if (result.ok) {
          return (result.json());
        } else {
          console.log("error getting chat token");
        }
      });
  }
  static createLiveStream(identifier) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}create_live_stream`,
          {
            method: 'POST',
            headers: {
              'Cache-Control': 'no-cache',
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ identifier: identifier }),
          },
        );
      })
      .then(jsonOrThrow);
  }
  static completeLiveStream(identifier) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        return fetch(
          `${DOMAIN}${projectConfig.apiVersion}complete_live_stream`,
          {
            method: 'POST',
            headers: {
              'Cache-Control': 'no-cache',
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ identifier: identifier }),
          },
        );
      })
      .then(jsonOrThrow);
  }
  static getWorkspaceLeaderboardCsv(workspace_id, {startDate = null, endDate = null, challengeId = null} = {}) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        let url = `${DOMAIN}${projectConfig.apiVersion}analytics/workspace/${workspace_id}/leaderboard/csv`;
        if (startDate) url += `?challenge_start=${startDate}`;
        if (endDate) url += `${startDate ? '&' : '?'}challenge_end=${endDate}`;
        if (challengeId) url += `${startDate || endDate ? '&' : '?'}challenge_id=${challengeId}`;
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.blob();
        } else return Error('Unable to retrieve workspace leaderboard CSV');
      });
  }
  static getWorkspaceAttendeeCsv(workspace_id, {startDate = null, endDate = null, eventId = null} = {}) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        let url = `${DOMAIN}${projectConfig.apiVersion}analytics/workspace/${workspace_id}/attendee/csv`;
        if (startDate) url += `?event_date_start=${startDate}`;
        if (endDate) url += `${startDate ? '&' : '?'}event_date_end=${endDate}`;
        if (eventId) url += `${startDate || endDate ? '&' : '?'}event_id=${eventId}`;
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.blob();
        } else return Error('Unable to retrieve workspace attendee CSV');
      });
  }
  static getWorkspaceUserCsv(workspace_id, {startDate = null, endDate = null} = {}) {
    return authentication
      .getAccessToken()
      .then((accessToken) => {
        let url = `${DOMAIN}${projectConfig.apiVersion}analytics/workspace/${workspace_id}/user/csv`;
        if (startDate) url += `?date_joined_start=${startDate}`;
        if (endDate) url += `${startDate ? '&' : '?'}date_joined_end=${endDate}`;
        return fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${accessToken}`,
          },
        });
      })
      .then((response) => {
        if (response.status === 200) {
          return response.blob();
        } else return Error('Unable to retrieve workspace user CSV');
      });
  }
}
